import moment from 'moment';
import { CalendarIcon as CalendarIconSvg } from '../../icons/CalendarIcon';
import { formatDateISO, parseDateISO, todayISO } from '../../utils/dateHelpers';
import { formatCurrencyWhole } from '../../utils/formatCurrency';
import {
  CalendarIcon,
  DayCell,
  DayNumber,
  DayPrice,
  DaysRow,
  Header,
  MonthGrid,
  MonthLabel,
  NavButton,
  NavControls,
  WeekdayLabel,
  WeekdayRow,
  Wrapper,
} from './ReservationCalendar.styles';
import type { DayAvailability, ReservationCalendarProps } from './ReservationCalendar.types';

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]];
}

function getDefaultMonths(): string[] {
  return moment.localeData().months();
}

function getWeekStartDate(dateStr: string): Date {
  const date = parseDateISO(dateStr);
  const day = date.getDay();
  const offset = (day + 6) % 7;
  const start = new Date(date);
  start.setDate(start.getDate() - offset);
  return start;
}

function getMonthDays(year: number, month: number): (Date | null)[] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayJS = new Date(year, month, 1).getDay();
  const startOffset = (firstDayJS + 6) % 7;

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startOffset; i++) {
    cells.push(null);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(new Date(year, month, d));
  }
  return cells;
}

export function ReservationCalendar({
  activeDate,
  selectedDate,
  view = 'week',
  availability,
  onDateSelect,
  onNavigate,
  onViewChange,
  labels,
}: ReservationCalendarProps) {
  const isMonthView = view === 'month';

  const today = todayISO();
  const weekdays = labels?.weekdays ?? getDefaultWeekdays();
  const monthNames = labels?.months ?? getDefaultMonths();
  const locale = labels?.locale ?? 'en-GB';
  const prevWeekLabel = labels?.previousWeek ?? 'Previous week';
  const nextWeekLabel = labels?.nextWeek ?? 'Next week';
  const prevMonthLabel = labels?.previousMonth ?? 'Previous month';
  const nextMonthLabel = labels?.nextMonth ?? 'Next month';
  const openCalendarLabel = labels?.openCalendar ?? 'Open calendar';
  const closeCalendarLabel = labels?.closeCalendar ?? 'Close calendar';

  const weekStart = getWeekStartDate(activeDate);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    days.push(d);
  }

  const availMap = new Map<string, DayAvailability>();
  availability?.forEach((a) => availMap.set(a.date, a));

  const monthDate = parseDateISO(activeDate);
  const monthLabel = `${monthNames[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

  const todayDate = parseDateISO(today);
  const isPrevDisabled = isMonthView
    ? monthDate.getFullYear() === todayDate.getFullYear() &&
      monthDate.getMonth() === todayDate.getMonth()
    : weekStart <= todayDate;

  const handlePrev = () => {
    if (isMonthView) {
      const prev = new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1);
      onNavigate(formatDateISO(prev));
    } else {
      const prev = new Date(weekStart);
      prev.setDate(prev.getDate() - 7);
      onNavigate(formatDateISO(prev));
    }
  };

  const handleNext = () => {
    if (isMonthView) {
      const next = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1);
      onNavigate(formatDateISO(next));
    } else {
      const next = new Date(weekStart);
      next.setDate(next.getDate() + 7);
      onNavigate(formatDateISO(next));
    }
  };

  const handleCalendarToggle = () => {
    onViewChange?.(isMonthView ? 'week' : 'month');
  };

  const renderDayCell = (date: Date | null, index: number) => {
    if (!date) {
      return (
        <DayCell
          key={`empty-${index}`}
          type="button"
          data-empty=""
          disabled
          aria-hidden="true"
          tabIndex={-1}
        />
      );
    }

    const dateStr = formatDateISO(date);
    const isPast = dateStr < today;
    const isSelected = dateStr === selectedDate;
    const isToday = dateStr === today;

    if (isMonthView) {
      return (
        <DayCell
          key={dateStr}
          type="button"
          {...(isPast ? { 'data-unavailable': '' } : undefined)}
          {...(isSelected ? { 'data-selected': '' } : undefined)}
          {...(isToday ? { 'data-today': '' } : undefined)}
          onClick={() => !isPast && onDateSelect(dateStr)}
          disabled={isPast}
          aria-label={dateStr}
          aria-pressed={isSelected}
        >
          <DayNumber $selected={isSelected}>{date.getDate()}</DayNumber>
        </DayCell>
      );
    }

    const dayData = availMap.get(dateStr);
    const isUnavailable = isPast || (dayData !== undefined && !dayData.available);
    const showPrice =
      !isUnavailable && dayData?.price !== undefined && dayData.price > 0 && dayData.currencyCode;

    return (
      <DayCell
        key={dateStr}
        type="button"
        {...(isUnavailable ? { 'data-unavailable': '' } : undefined)}
        {...(isSelected ? { 'data-selected': '' } : undefined)}
        {...(isToday ? { 'data-today': '' } : undefined)}
        onClick={() => !isUnavailable && onDateSelect(dateStr)}
        disabled={isUnavailable}
        aria-label={dateStr}
        aria-pressed={isSelected}
      >
        <DayNumber $selected={isSelected}>{date.getDate()}</DayNumber>
        {showPrice && (
          <DayPrice $selected={isSelected}>
            {formatCurrencyWhole(dayData!.price!, dayData!.currencyCode!, locale)}
          </DayPrice>
        )}
      </DayCell>
    );
  };

  const monthCells = isMonthView ? getMonthDays(monthDate.getFullYear(), monthDate.getMonth()) : [];

  return (
    <Wrapper>
      <Header>
        <MonthLabel>{monthLabel}</MonthLabel>
        <NavControls>
          <NavButton
            type="button"
            data-cy="calendar-nav-prev"
            aria-label={isMonthView ? prevMonthLabel : prevWeekLabel}
            onClick={handlePrev}
            disabled={isPrevDisabled}
          >
            &#8249;
          </NavButton>
          <NavButton
            type="button"
            data-cy="calendar-nav-next"
            aria-label={isMonthView ? nextMonthLabel : nextWeekLabel}
            onClick={handleNext}
          >
            &#8250;
          </NavButton>
          {onViewChange && (
            <CalendarIcon
              type="button"
              aria-label={isMonthView ? closeCalendarLabel : openCalendarLabel}
              aria-pressed={isMonthView}
              onClick={handleCalendarToggle}
              {...(isMonthView ? { 'data-active': '' } : undefined)}
            >
              <CalendarIconSvg size={18} />
            </CalendarIcon>
          )}
        </NavControls>
      </Header>
      <WeekdayRow>
        {weekdays.map((wd) => (
          <WeekdayLabel key={wd}>{wd}</WeekdayLabel>
        ))}
      </WeekdayRow>
      {isMonthView ? (
        <MonthGrid>{monthCells.map((date, i) => renderDayCell(date, i))}</MonthGrid>
      ) : (
        <DaysRow>{days.map((date, i) => renderDayCell(date, i))}</DaysRow>
      )}
    </Wrapper>
  );
}
